import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/authStore'

// Environment-based API URL configuration
const getApiUrl = () => {
  // Check for environment variable first
  if ((import.meta as any).env.VITE_API_URL) {
    return (import.meta as any).env.VITE_API_URL
  }

  // Default to localhost for development
  if ((import.meta as any).DEV) {
    return 'http://localhost:8000/api'
  }

  // Production fallback
  return 'https://api.pexiblock.com/api'
}

export const API_URL = getApiUrl()

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const { tokens } = useAuthStore.getState()
        if (tokens?.access) {
          config.headers.Authorization = `Bearer ${tokens.access}`
        }
        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config
        
        // Log 400 errors for debugging
        if (error.response?.status === 400) {
          console.error('400 Bad Request Error:', {
            url: error.config?.url,
            method: error.config?.method,
            data: error.config?.data,
            response: error.response?.data,
          })
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            const { tokens } = useAuthStore.getState()
            if (tokens?.refresh) {
              const response = await axios.post(`${API_URL}/auth/refresh/`, {
                refresh: tokens.refresh,
              })

              const { tokens: newTokens } = response.data
              useAuthStore.setState({ tokens: newTokens })

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${newTokens.access}`
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            // Refresh failed, logout user
            useAuthStore.getState().logout()
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get(url, config)
    return response.data
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put(url, data, config)
    return response.data
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch(url, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete(url, config)
    return response.data
  }
}

export const apiClient = new ApiClient()
